import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Flame, Trophy, Share2, Download, X } from "lucide-react";

const PIECES = 46;
const COLORS = ["#c8ff00", "#ffffff", "#9bd100", "#f5a524", "#e9ff80"];

const copy = (s, first) => {
  if (s.current >= 4) return `${first}, you're unstoppable — ${s.current} weeks straight.`;
  if (s.current >= 1) return `Keep it rolling, ${first} — one session this week keeps the flame alive.`;
  if (s.broken) return `Your flame went out, ${first}. One session this week relights it.`;
  return `Finish your first session, ${first}, and your streak begins.`;
};

const Confetti = () => {
  const pieces = useMemo(() => Array.from({ length: PIECES }, (_, i) => ({ id: i, left: Math.random() * 100, delay: Math.random() * 0.8, dur: 2.2 + Math.random() * 1.4, color: COLORS[i % COLORS.length], rot: Math.random() * 360, w: 6 + Math.random() * 6 })), []);
  return (
    <div className="confetti" aria-hidden="true" data-testid="streak-confetti">
      {pieces.map((p) => <i key={p.id} style={{ left: `${p.left}%`, animationDelay: `${p.delay}s`, animationDuration: `${p.dur}s`, background: p.color, transform: `rotate(${p.rot}deg)`, width: p.w, height: p.w * 0.45 }} />)}
    </div>
  );
};

const drawBadge = (canvas, weeks, name, studio) => {
  const W = 1080, H = 1080, ctx = canvas.getContext("2d");
  canvas.width = W; canvas.height = H;
  ctx.fillStyle = "#0a0a0a"; ctx.fillRect(0, 0, W, H);
  const g = ctx.createRadialGradient(W / 2, H / 2, 80, W / 2, H / 2, 620); g.addColorStop(0, "rgba(200,255,0,.28)"); g.addColorStop(1, "rgba(200,255,0,0)");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "#c8ff00"; ctx.lineWidth = 14; ctx.beginPath(); ctx.arc(W / 2, 430, 250, 0, Math.PI * 2); ctx.stroke();
  ctx.fillStyle = "#c8ff00"; ctx.textAlign = "center"; ctx.font = "900 230px Barlow Condensed, Oswald, Impact, sans-serif"; ctx.fillText(String(weeks), W / 2, 505);
  ctx.fillStyle = "#fff"; ctx.font = "800 64px Barlow Condensed, Oswald, Impact, sans-serif"; ctx.fillText("WEEK STREAK", W / 2, 780);
  ctx.fillStyle = "#c8ff00"; ctx.font = "700 34px Archivo, Inter, sans-serif"; ctx.fillText(name.toUpperCase(), W / 2, 850);
  ctx.fillStyle = "#6f6f6f"; ctx.font = "600 28px Archivo, Inter, sans-serif"; ctx.fillText(`${studio}  ·  Powered by SpottrOS`, W / 2, 1000);
};

const shareBadge = async (weeks, name, studio) => {
  const c = document.createElement("canvas"); drawBadge(c, weeks, name, studio);
  const blob = await new Promise((res) => c.toBlob(res, "image/png"));
  const file = new File([blob], `${weeks}-week-streak.png`, { type: "image/png" });
  if (navigator.canShare?.({ files: [file] })) { try { await navigator.share({ files: [file], title: `${weeks}-week streak`, text: `${weeks} weeks straight with ${studio} 🔥` }); return; } catch { return; } }
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = file.name; a.click();
  toast.success("Badge saved — post it to your story");
};

export const StreakCard = ({ streak, first, name, studio, clientId }) => {
  const key = streak?.milestone ? `spottr-milestone-${clientId}-${streak.milestone}` : null;
  const [celebrate, setCelebrate] = useState(false);
  useEffect(() => { if (key && !localStorage.getItem(key)) { setCelebrate(true); localStorage.setItem(key, "1"); } }, [key]);
  if (!streak) return null;
  const tier = streak.current >= 4 ? "hot" : streak.current >= 1 ? "warm" : "cold";
  return (
    <>
      <div className={`streak-card ${tier}`} data-testid="client-streak-card">
        <div className="flame-big"><Flame size={34} strokeWidth={2.2} fill={streak.current ? "currentColor" : "none"} /></div>
        <div className="streak-main">
          <small>Session streak</small>
          <b className="num" data-testid="client-streak-current">{streak.current}<span>wk{streak.current === 1 ? "" : "s"}</span></b>
          <p>{copy(streak, first)}</p>
          <div className="milestones" data-testid="streak-milestones">
            {streak.milestones.map((m) => <span key={m} className={`ms ${streak.current >= m ? "hit" : ""}`} data-testid={`milestone-${m}`}><Flame size={11} strokeWidth={2.6} fill={streak.current >= m ? "currentColor" : "none"} />{m} wk</span>)}
            {streak.nextMilestone && <span className="ms-next">{streak.nextMilestone - streak.current} more to {streak.nextMilestone}</span>}
          </div>
        </div>
        <div className="streak-side">
          <div className="streak-best" data-testid="client-streak-best"><Trophy size={14} strokeWidth={2.4} /><span>Best</span><b className="num">{streak.best}</b><small>wk{streak.best === 1 ? "" : "s"}</small></div>
          {streak.milestone && <button type="button" className="btn sm" onClick={() => shareBadge(streak.milestone, name, studio)} data-testid="share-streak-badge"><Share2 size={13} /> Share badge</button>}
        </div>
      </div>
      {celebrate && createPortal(
        <div className="celebrate" role="dialog" aria-label="Streak milestone" data-testid="streak-celebration" onClick={() => setCelebrate(false)}>
          <Confetti />
          <div className="celebrate-card" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="x" onClick={() => setCelebrate(false)} aria-label="Close" data-testid="celebration-close"><X size={16} /></button>
            <div className="ring"><Flame size={40} strokeWidth={2.2} fill="currentColor" /></div>
            <small>Milestone unlocked</small>
            <b className="num">{streak.milestone}<span>-week streak</span></b>
            <p>{first}, {streak.milestone} weeks of showing up. That's not luck — that's who you are now.</p>
            <div className="actions">
              <button type="button" className="btn" onClick={() => shareBadge(streak.milestone, name, studio)} data-testid="celebration-share"><Share2 size={15} /> Share my badge</button>
              <button type="button" className="btn g" onClick={() => shareBadge(streak.milestone, name, studio)} data-testid="celebration-download"><Download size={15} /> Save</button>
            </div>
          </div>
        </div>, document.body)}
    </>
  );
};
